import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { getPlayCountryCode } from '../../modules/play-billing-config';
import { analytics } from './analytics';
import { api } from './api';

/**
 * CORE-1535 — можно ли этой сборке вести человека к внешней оплате.
 *
 * Одна точка решения на все экраны (подписка, программы, события):
 * - веб — да;
 * - iOS — нет, до отдельного решения;
 * - Android — только пользователю из России. Google Play не требует Play
 *   Billing для платежей пользователей в РФ (answer/11950272), а для остальных
 *   стран запрещает и продавать мимо него, и вести к внешней оплате
 *   (answer/9858738).
 *
 * Страна — аккаунт Google Play (getBillingConfigAsync), фолбэк — страна по
 * серверу (GeoIP). Неизвестно = не РФ.
 */
export type PaymentCountrySource = 'play' | 'server' | 'none';

export interface PaymentSurfaceDecision {
  allowed: boolean;
  country: string | null;
  source: PaymentCountrySource | 'platform';
}

export const EXTERNAL_PURCHASE_COUNTRY = 'RU';
const COUNTRY_TIMEOUT_MS = 5000;

/** Чистое правило — на нём держатся тесты. */
export function decideExternalPurchase(
  platform: string,
  playCountry: string | null,
  serverCountry: string | null,
): PaymentSurfaceDecision {
  if (platform === 'web') return { allowed: true, country: null, source: 'platform' };
  if (platform !== 'android') return { allowed: false, country: null, source: 'platform' };

  const play = playCountry?.trim().toUpperCase() || null;
  const server = serverCountry?.trim().toUpperCase() || null;
  const country = play ?? server;
  const source: PaymentCountrySource = play ? 'play' : server ? 'server' : 'none';
  return { allowed: country === EXTERNAL_PURCHASE_COUNTRY, country, source };
}

const withTimeout = <T,>(p: Promise<T>, fallback: T): Promise<T> =>
  Promise.race([p, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), COUNTRY_TIMEOUT_MS))]);

let pending: Promise<PaymentSurfaceDecision> | null = null;

/**
 * Решение на сессию: страну спрашиваем один раз, в аналитику пишем один раз
 * (payment_surface_decided — доказательство логики при проверке Google).
 * Сбой — «не РФ», и повторить попытку можно со следующего запуска.
 */
export function resolveExternalPurchase(): Promise<PaymentSurfaceDecision> {
  if (pending) return pending;
  pending = (async () => {
    if (Platform.OS !== 'android') return decideExternalPurchase(Platform.OS, null, null);

    const play = await withTimeout(getPlayCountryCode(), null);
    const server = play
      ? null
      : await withTimeout(api.getRegion().then((r) => r.country).catch(() => null), null);
    const decision = decideExternalPurchase(Platform.OS, play, server);
    analytics.track('payment_surface_decided', {
      country: decision.country,
      source: decision.source,
      allowed: decision.allowed,
      platform: Platform.OS,
    });
    return decision;
  })();
  return pending;
}

/** `null`, пока решение не принято: экран не показывает ни CTA, ни «недоступно». */
export function useCanShowExternalPurchase(): boolean | null {
  const [allowed, setAllowed] = useState<boolean | null>(Platform.OS === 'web' ? true : null);
  useEffect(() => {
    let alive = true;
    void resolveExternalPurchase().then((d) => { if (alive) setAllowed(d.allowed); });
    return () => { alive = false; };
  }, []);
  return allowed;
}

/** Только для тестов. */
export function __resetExternalPurchaseForTests(): void {
  pending = null;
}
